export const EDITOR_MESSAGE_TYPES = {
  ready: "slant-editor:ready",
  loaded: "slant-editor:loaded",
  dirty: "slant-editor:dirty",
  blur: "slant-editor:blur",
  history: "slant-editor:history",
  undo: "slant-editor:undo",
  redo: "slant-editor:redo",
  setEditable: "slant-editor:set-editable",
  requestHtml: "slant-editor:request-html",
  html: "slant-editor:html",
} as const

export const EDITOR_CONFIG_ELEMENT_ID = "slant-editor-config"

export interface DocumentEditRuntimeConfig {
  attachments: Record<string, string | null>
  loadId: number
}

export const DOCUMENT_EDIT_RUNTIME_STYLES = `
  html { scroll-padding-top: 48px; }
  body:focus { outline: none; }
  body * { user-select: text !important; -webkit-user-select: text !important; pointer-events: auto !important; }
  body a { cursor: pointer; }
  .slant-editor-hidden { display: none !important; }
  [data-slant-editor][hidden] { display: none !important; }
  [data-slant-editor="text-toolbar"] {
    position: absolute; z-index: 2147483647; display: flex; align-items: center; gap: 2px; padding: 4px;
    border: 1px solid #e5e5e5; border-radius: 8px; background: #ffffff; color: #0a0a0a;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    font: 500 12px/1 system-ui, -apple-system, "Segoe UI", sans-serif; user-select: none; -webkit-user-select: none;
  }
  [data-slant-editor="text-toolbar"] button {
    all: unset; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box;
    width: 28px; height: 28px; border-radius: 6px; cursor: pointer; color: #737373; font: inherit;
    transition: background-color 120ms ease, color 120ms ease;
  }
  [data-slant-editor="text-toolbar"] button:hover { background: #f5f5f5; color: #171717; }
  [data-slant-editor="text-toolbar"] button:focus-visible { outline: 2px solid #a3a3a3; outline-offset: 1px; }
  [data-slant-editor="text-toolbar"] button[aria-pressed="true"] { background: #f5f5f5; color: #171717; }
  [data-slant-editor="text-toolbar"] button svg { width: 14px; height: 14px; }
  [data-slant-editor="text-toolbar"] hr { width: 1px; height: 16px; margin: 0 4px; border: none; background: #e5e5e5; }
  [data-slant-editor="text-toolbar"] label { display: inline-flex; align-items: center; gap: 2px; color: #737373; font-size: 11px; }
  [data-slant-editor="text-toolbar"] input[data-role="font-size"] { width: 44px; height: 28px; padding: 0 4px; text-align: center; font-variant-numeric: tabular-nums; -moz-appearance: textfield; appearance: textfield; }
  [data-slant-editor="text-toolbar"] input[data-role="font-size"]::-webkit-inner-spin-button, [data-slant-editor="text-toolbar"] input[data-role="font-size"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  [data-slant-editor="text-toolbar"] input {
    all: unset; box-sizing: border-box; width: 200px; height: 28px; padding: 0 8px; border: 1px solid #e5e5e5; border-radius: 6px;
    background: #ffffff; color: #0a0a0a; font: 400 12px/1 system-ui, -apple-system, "Segoe UI", sans-serif;
  }
  [data-slant-editor="text-toolbar"] input:focus { border-color: #a3a3a3; box-shadow: 0 0 0 3px rgba(163, 163, 163, 0.25); }
  [data-slant-editor="text-toolbar"] input::placeholder { color: #737373; }
  [data-slant-editor="text-toolbar"] [data-role="link-form"] { display: flex; align-items: center; gap: 2px; }
  [data-slant-editor="text-toolbar"] [data-role="link-form"][hidden] { display: none !important; }
`

export const DOCUMENT_EDIT_RUNTIME_SCRIPT = `
(function () {
  var EDITOR_ATTR = "data-slant-editor";
  var MARGIN_TEXT_ATTR = "data-slant-margin-text";
  var TYPES = ${JSON.stringify(EDITOR_MESSAGE_TYPES)};
  var SKIP_TAGS = { HTML: 1, HEAD: 1, BODY: 1, STYLE: 1, SCRIPT: 1, TEMPLATE: 1, TITLE: 1, META: 1, LINK: 1, BR: 1, WBR: 1 };
  var HISTORY_LIMIT = 50;
  var REPAGINATE_DELAY_MS = 250;
  var repaginateTimer = null;
  var TEXT_HISTORY_GROUP_MS = 1000;
  var FONT_SIZE_STEP = 1.125;
  var MIN_FONT_PX = 6;
  var MAX_FONT_PX = 160;
  var TEXT_INPUT_TYPES = { insertText: 1, insertReplacementText: 1, insertCompositionText: 1, insertFromPaste: 1, deleteContentBackward: 1, deleteContentForward: 1, deleteWordBackward: 1, deleteWordForward: 1, deleteSoftLineBackward: 1, deleteSoftLineForward: 1, deleteHardLineBackward: 1, deleteHardLineForward: 1, deleteByCut: 1, deleteContent: 1, formatBold: 1, formatItalic: 1, formatUnderline: 1, historyUndo: 1, historyRedo: 1 };
  var configElement = document.getElementById(${JSON.stringify(EDITOR_CONFIG_ELEMENT_ID)});
  var config = configElement ? JSON.parse(configElement.textContent || "{}") : {};
  var editable = true;
  var loaded = false;
  var textToolbar = null;
  var linkForm = null;
  var linkInput = null;
  var sizeInput = null;
  var sizeRange = null;
  var savedRange = null;
  var undoStack = [];
  var redoStack = [];
  var lastHistoryKind = null;
  var lastHistoryAt = 0;

  function post(message) {
    window.parent.postMessage(message, "*");
  }

  function isEditorNode(node) {
    return !!(node && node.nodeType === 1 && node.closest("[" + EDITOR_ATTR + "]"));
  }

  function markDirty() {
    post({ type: TYPES.dirty, dirty: true });
  }

  function stripEditorState(node) {
    Array.prototype.forEach.call(node.querySelectorAll("[" + EDITOR_ATTR + "]"), function (child) { child.remove(); });
    Array.prototype.forEach.call(node.querySelectorAll("[" + MARGIN_TEXT_ATTR + "]"), function (child) { child.removeAttribute(MARGIN_TEXT_ATTR); });
    var images = Array.prototype.slice.call(node.querySelectorAll("[data-slant-src]"));
    if (node.hasAttribute && node.hasAttribute("data-slant-src")) images.push(node);
    images.forEach(function (image) {
      image.setAttribute("src", image.getAttribute("data-slant-src"));
      image.removeAttribute("data-slant-src");
      image.classList.remove("slant-editor-hidden");
    });
  }

  function snapshot() {
    var body = document.body.cloneNode(true);
    window.slantUnpaginate(body);
    return body.innerHTML;
  }

  // The caret's boundary points are copied out before the sheets are rebuilt:
  // a live Range is collapsed by the browser as the nodes around it move.
  function repaginate() {
    var selection = document.getSelection();
    var carets = [];
    if (selection && selection.rangeCount) {
      var current = selection.getRangeAt(0);
      carets = [
        { node: current.startContainer, offset: current.startOffset },
        { node: current.endContainer, offset: current.endOffset },
      ];
    }
    window.slantPaginate({ mode: "screen", carets: carets });
    if (carets.length && carets[0].node.isConnected && carets[1].node.isConnected) {
      var range = document.createRange();
      range.setStart(carets[0].node, Math.min(carets[0].offset, carets[0].node.length || carets[0].node.childNodes.length));
      range.setEnd(carets[1].node, Math.min(carets[1].offset, carets[1].node.length || carets[1].node.childNodes.length));
      selection.removeAllRanges();
      selection.addRange(range);
    }
    updateTextToolbar();
  }

  function scheduleRepaginate() {
    if (repaginateTimer) clearTimeout(repaginateTimer);
    repaginateTimer = setTimeout(function () {
      repaginateTimer = null;
      repaginate();
    }, REPAGINATE_DELAY_MS);
  }

  function postHistory() {
    post({ type: TYPES.history, canUndo: undoStack.length > 0, canRedo: redoStack.length > 0 });
  }

  function recordHistory(kind) {
    var now = Date.now();
    if (kind === "text" && lastHistoryKind === "text" && now - lastHistoryAt < TEXT_HISTORY_GROUP_MS) {
      lastHistoryAt = now;
      return;
    }
    undoStack.push(snapshot());
    if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
    redoStack = [];
    lastHistoryKind = kind;
    lastHistoryAt = now;
    postHistory();
  }

  function restore(html) {
    hideTextToolbar();
    document.body.innerHTML = html;
    window.slantPaginate({ mode: "screen" });
    lastHistoryKind = null;
    markDirty();
    postHistory();
  }

  function undo() {
    if (!undoStack.length) return;
    redoStack.push(snapshot());
    restore(undoStack.pop());
  }

  function redo() {
    if (!redoStack.length) return;
    undoStack.push(snapshot());
    restore(redoStack.pop());
  }

  function imagesDecoded() {
    return Promise.all(
      Array.prototype.map.call(document.images, function (image) {
        return image.decode ? image.decode().catch(function () {}) : Promise.resolve();
      })
    );
  }

  function applyAttachments() {
    var attachments = config.attachments || {};
    Array.prototype.forEach.call(document.querySelectorAll("img[src]"), function (image) {
      var source = image.getAttribute("src");
      if (!(source in attachments)) return;
      image.setAttribute("data-slant-src", source);
      var uri = attachments[source];
      if (uri) image.setAttribute("src", uri);
      else {
        image.removeAttribute("src");
        image.classList.add("slant-editor-hidden");
      }
    });
  }

  // Editing only opens once fonts are ready and the sheets are laid out on
  // the frame's final assignment; until then the flag is just remembered.
  function setEditable(flag) {
    editable = flag;
    if (!loaded) return;
    document.body.setAttribute("contenteditable", editable ? "true" : "false");
    if (!editable) hideTextToolbar();
  }

  function hideTextToolbar() {
    if (!textToolbar) return;
    textToolbar.hidden = true;
    linkForm.hidden = true;
    savedRange = null;
    sizeRange = null;
  }

  function textBlockFor(node) {
    var element = node && node.nodeType === 1 ? node : node && node.parentElement;
    while (element && element !== document.body) {
      var display = getComputedStyle(element).display;
      if (display !== "inline" && display !== "contents") return element;
      element = element.parentElement;
    }
    return document.body;
  }

  // "start": nothing before the range start in its block. "end": nothing after
  // the range end in its block. "end-start": the range end sits at the very
  // start of its block.
  function atBlockEdge(range, edge) {
    var block = textBlockFor(edge === "start" ? range.startContainer : range.endContainer);
    var probe = document.createRange();
    if (edge === "start") {
      probe.setStart(block, 0);
      probe.setEnd(range.startContainer, range.startOffset);
    } else if (edge === "end-start") {
      probe.setStart(block, 0);
      probe.setEnd(range.endContainer, range.endOffset);
    } else {
      probe.setStart(range.endContainer, range.endOffset);
      probe.setEnd(block, block.childNodes.length);
    }
    return probe.toString().length === 0;
  }

  function normalizeSelection() {
    var selection = document.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    var range = selection.getRangeAt(0);
    var startBlock = textBlockFor(range.startContainer);
    var endBlock = textBlockFor(range.endContainer);
    if (startBlock === endBlock || startBlock === document.body || !atBlockEdge(range, "end-start")) return;
    var trimmed = range.cloneRange();
    trimmed.setEnd(startBlock, startBlock.childNodes.length);
    selection.removeAllRanges();
    selection.addRange(trimmed);
  }

  function allowedTextInput(event) {
    var inputType = event.inputType || "";
    if (!TEXT_INPUT_TYPES[inputType]) return false;
    normalizeSelection();
    var selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    var range = selection.getRangeAt(0);
    if (textBlockFor(range.startContainer) !== textBlockFor(range.endContainer)) return false;
    if (range.collapsed && /^delete/.test(inputType)) {
      if (/Backward$/.test(inputType) && atBlockEdge(range, "start")) return false;
      if (/Forward$/.test(inputType) && atBlockEdge(range, "end")) return false;
    }
    return true;
  }

  function currentTextRange() {
    var selection = document.getSelection();
    if (!editable || !selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
    var range = selection.getRangeAt(0);
    var container = range.commonAncestorContainer;
    var element = container.nodeType === 1 ? container : container.parentElement;
    if (!element || !document.body.contains(element) || isEditorNode(element)) return null;
    if (textBlockFor(range.startContainer) !== textBlockFor(range.endContainer)) return null;
    return range;
  }

  function updateTextToolbar() {
    var range = currentTextRange();
    var rect = range && range.getBoundingClientRect();
    if (!rect || (!rect.width && !rect.height)) {
      hideTextToolbar();
      return;
    }
    textToolbar.hidden = false;
    ["bold", "italic", "underline"].forEach(function (command) {
      textToolbar.querySelector('[data-action="' + command + '"]').setAttribute("aria-pressed", String(document.queryCommandState(command)));
    });
    var anchor = range.commonAncestorContainer.nodeType === 1 ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
    textToolbar.querySelector('[data-action="link"]').setAttribute("aria-pressed", String(!!(anchor && anchor.closest("a"))));
    if (document.activeElement !== sizeInput) sizeInput.value = formatPoints(selectionFontPx(range)).replace("pt", "");
    var width = textToolbar.offsetWidth;
    var height = textToolbar.offsetHeight;
    var left = rect.left + rect.width / 2 + window.scrollX - width / 2;
    left = Math.max(4, Math.min(left, document.documentElement.scrollWidth - width - 4));
    var top = rect.top + window.scrollY - height - 8;
    if (top < window.scrollY + 4) top = rect.bottom + window.scrollY + 8;
    textToolbar.style.left = left + "px";
    textToolbar.style.top = top + "px";
  }

  function selectionFontPx(range) {
    var node = range.startContainer;
    var element = node.nodeType === 1 ? node : node.parentElement;
    if (element && element.nodeType === 1 && range.startContainer.nodeType === 1 && range.startContainer.childNodes[range.startOffset]) {
      var child = range.startContainer.childNodes[range.startOffset];
      element = child.nodeType === 1 ? child : child.parentElement;
    }
    return parseFloat(getComputedStyle(element).fontSize) || 16;
  }

  function formatPoints(px) {
    var points = Math.round(px * 0.75 * 2) / 2;
    return (points % 1 === 0 ? String(points) : points.toFixed(1)) + "pt";
  }

  function changeFontSize(direction) {
    var range = currentTextRange();
    if (!range) return;
    applyFontSizePx(selectionFontPx(range) * (direction > 0 ? FONT_SIZE_STEP : 1 / FONT_SIZE_STEP), null);
  }

  function commitFontSize() {
    var range = sizeRange;
    var points = parseFloat(sizeInput.value);
    sizeRange = null;
    sizeInput.blur();
    if (!range) {
      updateTextToolbar();
      return;
    }
    var selection = document.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    if (isFinite(points) && points > 0) applyFontSizePx(points / 0.75, null);
    else updateTextToolbar();
  }

  function applyFontSizePx(px, range) {
    var selection = document.getSelection();
    if (range) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    if (!currentTextRange()) return;
    var nextPx = Math.min(MAX_FONT_PX, Math.max(MIN_FONT_PX, px));
    recordHistory("format");
    var existing = Array.prototype.slice.call(document.body.querySelectorAll('font[size="7"]'));
    document.execCommand("fontSize", false, "7");
    var spans = [];
    Array.prototype.forEach.call(document.body.querySelectorAll('font[size="7"]'), function (font) {
      if (existing.indexOf(font) !== -1) return;
      var span = document.createElement("span");
      span.style.fontSize = formatPoints(nextPx);
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
      var parent = span.parentElement;
      if (parent && parent.tagName === "SPAN" && parent.childNodes.length === 1 && parent.style.length === 1 && parent.style.fontSize) {
        parent.style.fontSize = span.style.fontSize;
        while (span.firstChild) parent.appendChild(span.firstChild);
        span.remove();
        span = parent;
      }
      spans.push(span);
    });
    if (spans.length) {
      var restored = document.createRange();
      restored.setStartBefore(spans[0]);
      restored.setEndAfter(spans[spans.length - 1]);
      selection.removeAllRanges();
      selection.addRange(restored);
    }
    markDirty();
    scheduleRepaginate();
    updateTextToolbar();
  }

  function formatSelection(command) {
    if (!currentTextRange()) return;
    recordHistory("format");
    document.execCommand(command, false, null);
    markDirty();
    scheduleRepaginate();
    updateTextToolbar();
  }

  function openLinkForm() {
    var range = currentTextRange();
    if (!range) return;
    var anchor = range.commonAncestorContainer.nodeType === 1 ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
    if (anchor && anchor.closest("a")) {
      recordHistory("format");
      document.execCommand("unlink", false, null);
      markDirty();
      scheduleRepaginate();
      updateTextToolbar();
      return;
    }
    savedRange = range.cloneRange();
    linkInput.value = "";
    linkForm.hidden = false;
    linkInput.focus();
  }

  function closeLinkForm() {
    linkForm.hidden = true;
    var selection = document.getSelection();
    if (savedRange && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }
    savedRange = null;
    updateTextToolbar();
  }

  // A bare address such as example.com becomes https://example.com so the
  // link resolves outside the document instead of relative to it.
  function normalizeUrl(value) {
    var url = (value || "").trim();
    if (!url || /^(https?|mailto|tel):/i.test(url) || url[0] === "#" || url[0] === "/") return url;
    return "https://" + url;
  }

  function applyLink(value) {
    var url = normalizeUrl(value);
    if (!url || !savedRange) {
      closeLinkForm();
      return;
    }
    var selection = document.getSelection();
    selection.removeAllRanges();
    selection.addRange(savedRange);
    recordHistory("format");
    document.execCommand("createLink", false, url);
    markDirty();
    scheduleRepaginate();
    savedRange = null;
    linkForm.hidden = true;
    updateTextToolbar();
  }

  function installEditor() {
    textToolbar = document.createElement("div");
    textToolbar.setAttribute(EDITOR_ATTR, "text-toolbar");
    textToolbar.setAttribute("contenteditable", "false");
    textToolbar.hidden = true;
    textToolbar.innerHTML =
      '<button type="button" data-action="bold" title="Bold" aria-label="Bold"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8"/></svg></button>' +
      '<button type="button" data-action="italic" title="Italic" aria-label="Italic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/></svg></button>' +
      '<button type="button" data-action="underline" title="Underline" aria-label="Underline"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" x2="20" y1="20" y2="20"/></svg></button>' +
      '<button type="button" data-action="link" title="Link" aria-label="Link"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg></button>' +
      '<hr />' +
      '<button type="button" data-action="smaller" title="Smaller text" aria-label="Smaller text"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/></svg></button>' +
      '<label><input type="number" data-role="font-size" min="4" max="120" step="0.5" aria-label="Font size in points" />pt</label>' +
      '<button type="button" data-action="larger" title="Larger text" aria-label="Larger text"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg></button>' +
      '<span data-role="link-form" hidden><input type="url" placeholder="https://" aria-label="Link URL" /><button type="button" data-action="apply-link" title="Apply link" aria-label="Apply link"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg></button><button type="button" data-action="cancel-link" title="Cancel" aria-label="Cancel"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button></span>';
    linkForm = textToolbar.querySelector('[data-role="link-form"]');
    linkInput = linkForm.querySelector("input");
    sizeInput = textToolbar.querySelector('[data-role="font-size"]');
    document.documentElement.appendChild(textToolbar);

    textToolbar.addEventListener("mousedown", function (event) {
      if (event.target.tagName !== "INPUT") event.preventDefault();
    });
    textToolbar.addEventListener("click", function (event) {
      var control = event.target.closest("button");
      if (!control) return;
      event.preventDefault();
      var action = control.getAttribute("data-action");
      if (action === "link") openLinkForm();
      else if (action === "apply-link") applyLink(linkInput.value);
      else if (action === "cancel-link") closeLinkForm();
      else if (action === "smaller") changeFontSize(-1);
      else if (action === "larger") changeFontSize(1);
      else if (action) formatSelection(action);
    });
    // The sandboxed frame cannot submit forms, so Enter and the apply button
    // are handled directly.
    linkInput.addEventListener("keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
        applyLink(linkInput.value);
      } else if (event.key === "Escape") {
        event.preventDefault();
        closeLinkForm();
      }
    });
    document.addEventListener("selectionchange", function () {
      if ((linkForm && !linkForm.hidden) || sizeRange) return;
      if (editable) normalizeSelection();
      updateTextToolbar();
    });
    sizeInput.addEventListener("focus", function () {
      var range = currentTextRange();
      sizeRange = range ? range.cloneRange() : null;
      sizeInput.select();
    });
    sizeInput.addEventListener("keydown", function (event) {
      if (event.key === "Enter" || event.keyCode === 13) {
        event.preventDefault();
        commitFontSize();
      } else if (event.key === "Escape") {
        event.preventDefault();
        sizeInput.value = "";
        commitFontSize();
      }
    });
    sizeInput.addEventListener("change", function () {
      if (sizeRange) commitFontSize();
    });
    sizeInput.addEventListener("blur", function () {
      if (sizeRange) commitFontSize();
    });

    document.addEventListener("beforeinput", function (event) {
      if (isEditorNode(event.target)) return;
      if (!editable || !allowedTextInput(event)) {
        event.preventDefault();
        return;
      }
      recordHistory(/^format/.test(event.inputType || "") ? "format" : "text");
    });
    document.addEventListener("input", function (event) {
      if (isEditorNode(event.target)) return;
      markDirty();
      scheduleRepaginate();
    });
    document.addEventListener("paste", function (event) {
      if (isEditorNode(event.target)) return;
      event.preventDefault();
      if (!editable || !allowedTextInput({ inputType: "insertFromPaste" })) return;
      var text = event.clipboardData ? event.clipboardData.getData("text/plain") : "";
      text = text.replace(/\\s*\\n+\\s*/g, " ").trim();
      if (!text) return;
      recordHistory("text");
      document.execCommand("insertText", false, text);
    });
    document.addEventListener("dragover", function (event) { event.preventDefault(); });
    document.addEventListener("drop", function (event) { event.preventDefault(); });
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        hideTextToolbar();
        return;
      }
      if (!editable || !(event.metaKey || event.ctrlKey)) return;
      var key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (key === "y") {
        event.preventDefault();
        redo();
      }
    });
  }

  // A click on a link opens it in a new tab as a real link navigation, within
  // the click's own user activation so browsers treat it as the advisor's
  // click; the document's anchor itself never navigates the frame. Dragging
  // to select link text does not open it.
  document.addEventListener("click", function (event) {
    var anchor = event.target.closest && event.target.closest("a");
    if (!anchor) return;
    event.preventDefault();
    var selection = document.getSelection();
    if (selection && !selection.isCollapsed) return;
    var href = anchor.getAttribute("href") || "";
    if (!/^(https?:|mailto:)/i.test(href)) return;
    var opener = document.createElement("a");
    opener.href = href;
    opener.target = "_blank";
    opener.rel = "noopener noreferrer";
    opener.click();
  });
  window.addEventListener("blur", function () {
    post({ type: TYPES.blur });
  });

  // Running headers in @page margin boxes repeat body values such as the
  // title and the prepared date. Each such string is paired with the first
  // body element that carries the same text, and rewritten when that text is
  // edited.
  function pageRuleBlocks(css) {
    var blocks = [];
    var quote = null;
    var depth = 0;
    var pending = false;
    var open = -1;
    var openDepth = 0;
    for (var i = 0; i < css.length; i++) {
      var char = css[i];
      if (quote) {
        if (char === "\\\\") i += 1;
        else if (char === quote) quote = null;
      } else if (char === "/" && css[i + 1] === "*") {
        var close = css.indexOf("*/", i + 2);
        i = close === -1 ? css.length : close + 1;
      } else if (char === '"' || char === "'") {
        quote = char;
      } else if (open === -1 && /^@page\\b/.test(css.substr(i, 6))) {
        pending = true;
        i += 4;
      } else if (char === "{") {
        depth += 1;
        if (pending) {
          open = i;
          openDepth = depth;
          pending = false;
        }
      } else if (char === "}") {
        if (open !== -1 && depth === openDepth) {
          blocks.push(css.slice(open, i + 1));
          open = -1;
        }
        depth -= 1;
      }
    }
    return blocks;
  }

  var MARGIN_LITERAL = /content\\s*:[^;}]*?(["'])((?:\\\\[\\s\\S]|(?!\\1)[^\\\\\\n])*)\\1/g;

  function decodeCssString(raw) {
    return raw
      .replace(/\\\\\\n/g, "")
      .replace(/\\\\([0-9A-Fa-f]{1,6})\\s?/g, function (_, hex) {
        var code = parseInt(hex, 16);
        return code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff) ? String.fromCharCode(65533) : String.fromCodePoint(code);
      })
      .replace(/\\\\(.)/g, "$1");
  }

  function marginBoxLiterals() {
    var css = Array.prototype.map.call(document.querySelectorAll("style"), function (style) { return style.textContent; }).join("\\n");
    var strings = [];
    pageRuleBlocks(css).forEach(function (block) {
      var literal = new RegExp(MARGIN_LITERAL.source, "g");
      var match;
      while ((match = literal.exec(block))) {
        var text = decodeCssString(match[2]);
        if (/[\\p{L}\\p{N}]/u.test(text) && strings.indexOf(text) === -1) strings.push(text);
      }
    });
    return strings;
  }

  // Each running-header literal is bound through an attribute to the deepest
  // element of its first body occurrence, so the binding survives undo and
  // redo and tolerates inline formatting inside the title.
  function pairMarginBoxTexts() {
    var elements = Array.prototype.filter.call(document.body.querySelectorAll("*"), function (element) {
      return !isEditorNode(element) && !SKIP_TAGS[element.tagName];
    });
    marginBoxLiterals().forEach(function (text) {
      var matches = elements.filter(function (candidate) { return candidate.textContent.trim() === text.trim(); });
      var element = matches[0];
      if (!element) return;
      var inner;
      while ((inner = matches.filter(function (candidate) { return candidate.parentElement === element; })[0])) element = inner;
      element.setAttribute(MARGIN_TEXT_ATTR, text);
    });
  }

  function cssString(text, quote) {
    return quote + text.replace(/\\\\/g, "\\\\\\\\").split(quote).join("\\\\" + quote) + quote;
  }

  function syncMarginBoxTexts(root) {
    Array.prototype.forEach.call(root.querySelectorAll("[" + MARGIN_TEXT_ATTR + "]"), function (element) {
      var original = element.getAttribute(MARGIN_TEXT_ATTR);
      var current = element.textContent.trim();
      if (current === original.trim()) return;
      var updated = original.replace(/^(\\s*)[\\s\\S]*?(\\s*)$/, function (_, lead, trail) { return lead + current + trail; });
      Array.prototype.forEach.call(root.querySelectorAll("style"), function (style) {
        pageRuleBlocks(style.textContent).forEach(function (block) {
          var rewritten = block.replace(new RegExp(MARGIN_LITERAL.source, "g"), function (whole, quote, raw) {
            if (decodeCssString(raw) !== original) return whole;
            return whole.slice(0, whole.length - raw.length - 2) + cssString(updated, quote);
          });
          style.textContent = style.textContent.split(block).join(rewritten);
        });
      });
    });
  }

  function serialize() {
    var root = document.documentElement.cloneNode(true);
    window.slantUnpaginate(root);
    syncMarginBoxTexts(root);
    var body = root.querySelector("body");
    if (body) {
      body.removeAttribute("contenteditable");
      body.removeAttribute("spellcheck");
    }
    stripEditorState(root);
    var doctype = document.doctype ? "<!DOCTYPE " + document.doctype.name + ">\\n" : "";
    return doctype + root.outerHTML;
  }

  window.addEventListener("message", function (event) {
    if (event.source !== window.parent) return;
    var data = event.data;
    if (!data) return;
    if (data.type === TYPES.requestHtml) post({ type: TYPES.html, requestId: data.requestId, html: serialize() });
    else if (data.type === TYPES.undo) undo();
    else if (data.type === TYPES.redo) redo();
    else if (data.type === TYPES.setEditable) setEditable(!!data.editable);
  });

  applyAttachments();
  pairMarginBoxTexts();
  Array.prototype.forEach.call(document.body.querySelectorAll("[contenteditable]"), function (element) {
    if (!isEditorNode(element)) element.removeAttribute("contenteditable");
  });
  installEditor();
  document.body.setAttribute("spellcheck", "false");
  post({ type: TYPES.ready, loadId: config.loadId });
  Promise.all([document.fonts ? document.fonts.ready : Promise.resolve(), imagesDecoded()]).then(function () {
    window.slantPaginate({ mode: "screen" });
    loaded = true;
    setEditable(editable);
    postHistory();
    post({ type: TYPES.loaded });
  });
})();
`
